import React, {ChangeEvent, useCallback, useRef, useState} from 'react';
import cx from 'classnames';
import {
    defaultDropDownV2Context,
    DropdownItem,
    DropdownV2,
    DropDownV2Context,
    isSelected,
    Scrollbar,
    Text,
    useKeyboardNavigation
} from '@priority-software/priority-style-react';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {priority} from '@priority-software/priority-proto-api/lib';
import {useSearch} from './hooks/useSearch';
import {AutomationChoose} from '../../../../reducer/model';
import {isRTL} from '../../../../../../Localization';
import {texts} from '../../../../texts';

import {getSearchCategoryNameByType, optionMapper} from './utils';
import styles from './styles.module.scss';
import {SearchContentItem} from './parts/SearchControlParts/SearchContentItem/SearchContentItem';
import {SearchHeader} from './parts/SearchControlParts/SearchHeader';

type Props = {
    isOpen: boolean;
    contentComponentClasses?: string;
    onOpen: () => void;
    onClose: () => void;
    onSelect: (option: AutomationChoose) => void;
    searchPlaceholder?: string;
    session: string;
    value: string;
    relatedField: number;
    trigger: React.ReactElement;
    openingSide?: 'leftSide' | 'rightSide';
    onCriticalError: () => void;
    portalSelector: string;
    requestDataHandler?: any;
};

const eventTypes = ['touchstart', 'mousedown'];

export const AutomationFieldSearch = (props: Props) => {
    const {
        contentComponentClasses,
        session,
        trigger,
        relatedField,
        value = '',
        isOpen,
        onOpen,
        openingSide,
        onClose,
        onSelect,
        onCriticalError,
        portalSelector,
        requestDataHandler
    } = props;
    const inputRef = useRef<HTMLInputElement>();

    const [
        {
            results: options,
            Pattern: searchQuery,
            IgnoreCase: ignoreCase,
            FromStart: searchFromBeginning,
            hasClientSearch,
            searchType
        },
        handlers
    ] = useSearch(relatedField, session, value, optionMapper, onCriticalError, requestDataHandler);

    const [isChooseType, setIsChooseType] = useState(false);

    const {
        handleSearchOpen,
        handleSearchFromBeginningChange,
        handleIgnoreCaseChange,
        handleAsyncSearchChange,
        handleScroll,
        handleSearchQueryChange,
        handleClientSearchChange,
        handleSearchCategoryChange
    } = handlers;

    const handleEnter = useCallback(
        (e: KeyboardEvent, focusedIndex: number) => {
            const focusedOption = options[focusedIndex];

            if (focusedOption) {
                onSelect(focusedOption);
            }
        },
        [onSelect, options]
    );

    const {focusedIndex, handleNavigationKeys, setFocusedIndex} = useKeyboardNavigation({
        optionsCount: options.length,
        defaultFocusedIndex: 0,
        isOpen,
        handleEnter
    });

    const searchByCategory =
        searchType === priority.netitems.shared.FieldSelectType.SearchName ||
        searchType === priority.netitems.shared.FieldSelectType.SearchDes ||
        searchType === priority.netitems.shared.FieldSelectType.SearchEdes;

    const openDropdown = useCallback(async () => {
        const res = await handleSearchOpen(value);
        const defaultFocusedIndex = res.Values.findIndex(item => item.Value === value) || 0;

        setFocusedIndex(defaultFocusedIndex);
        setIsChooseType(
            res.Type === priority.netitems.shared.FieldSelectType.Choose || res.Type === undefined
        );
        onOpen();
    }, [onOpen, value, handleSearchOpen, setFocusedIndex]);

    const onCategoryChange = async () => {
        await handleSearchCategoryChange();
        setFocusedIndex(0);
    };

    const toggleOpen = useCallback(async () => {
        if (isOpen) {
            onClose();
        } else {
            await openDropdown();
        }
    }, [isOpen, onClose, openDropdown]);

    const contentRenderer = useCallback(
        (data: DropdownItemData) => {
            const searchAll = searchType === priority.netitems.shared.FieldSelectType.SearchAll;
            return (
                <SearchContentItem
                    searchQuery={searchQuery}
                    searchByCategory={!!searchByCategory}
                    searchFromBeginning={searchFromBeginning}
                    itemData={data}
                    ignorecase={ignoreCase}
                    searchAll={searchAll}
                />
            );
        },
        [searchQuery, searchFromBeginning, ignoreCase, searchType, searchByCategory]
    );

    const handleSearch = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const searchString = e.target.value;

            setFocusedIndex(0);
            if (hasClientSearch) {
                handleClientSearchChange(searchString);
            } else {
                handleSearchQueryChange(searchString);
                await handleAsyncSearchChange(searchString);
            }
        },
        [
            handleAsyncSearchChange,
            setFocusedIndex,
            handleSearchQueryChange,
            handleClientSearchChange,
            hasClientSearch
        ]
    );

    const hasSearch = isChooseType ? hasClientSearch : true;

    return (
        <DropDownV2Context.Provider value={{...defaultDropDownV2Context, isOpen, toggleOpen}}>
            <DropdownV2
                usePortal
                onClose={onClose}
                isOpen={isOpen}
                onOpen={openDropdown}
                onKeyDown={handleNavigationKeys}
                contentComponentClasses={cx(contentComponentClasses, styles.dropdownMenu, {
                    [styles.hasExtraPadding]: hasSearch
                })}
                portalSelector={portalSelector}
                trigger={trigger}
                openingSide={openingSide}
                eventTypes={eventTypes}
            >
                <SearchHeader
                    hasSearch={hasSearch}
                    inputRef={inputRef}
                    searchQuery={searchQuery}
                    ignorecase={ignoreCase}
                    handleSearch={handleSearch}
                    searchFromBeginning={searchFromBeginning}
                    searchByTitle={getSearchCategoryNameByType(searchType)}
                    isSearchAsync={!isChooseType}
                    handleIgnoreCaseChange={handleIgnoreCaseChange}
                    handleSearchFromBeginningChange={handleSearchFromBeginningChange}
                    handleSearchByChange={onCategoryChange}
                />

                {isOpen && (
                    <div>
                        <Scrollbar
                            autoHeightMax={300}
                            autoHeight
                            rtl={isRTL()}
                            onScrollEnd={handleScroll}
                            shiftVerticalScrollPositionX={6}
                        >
                            <div className="pui-dropdown-options">
                                {options.length > 0 ? (
                                    options.map((item, index) => (
                                        <DropdownItem
                                            key={`${item.value}-${item.label}-${item.description}`}
                                            itemBorder
                                            data={item}
                                            onSelect={onSelect}
                                            isFocused={focusedIndex === index}
                                            isSelected={isSelected(item, value, false)}
                                            contentRenderer={
                                                !isChooseType ? contentRenderer : undefined
                                            }
                                        />
                                    ))
                                ) : (
                                    <Text
                                        componentClasses={styles.noOptions}
                                        skin="paragraph-2"
                                        tag="div"
                                        textColor="secondary-400"
                                    >
                                        {texts.no_options_text}
                                    </Text>
                                )}
                            </div>
                        </Scrollbar>
                    </div>
                )}
            </DropdownV2>
        </DropDownV2Context.Provider>
    );
};
